import React, { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import { useDataLoader } from "react-remote-data-hooks";
import api from "../../../api";
import classnames from "classnames";
import {
  City,
  CityLocation,
  CityZone,
  ListSort,
  PaginatedApiResponse,
  SortOrder,
  State,
  setProposalTabStatus,
} from "../../../core";
import { Project, Shortlist } from "../../../core/api/models/project";
import { LeadRequirement } from "../../../core/api/models/requirement";
import PropertyTabs from "../../requirement-detail-popup/PropertyTabs";
import SearchProperty from "../../requirement-detail-popup/searchProperty";
import { PropertyTabNames } from "./props";
import SearchRequirement from "./Search";
import { ContainerLoader } from "../../../components/loader";
import { diff } from "json-diff";
import { toastr } from "react-redux-toastr";
import ShortListedProperty from "../../requirement-detail-popup/shortListedProperty";
import UserLogs from "../../../components/logs/user-logs";
import CreateProposal from "../../create-proposal";
import { useDispatch, useSelector } from "react-redux";

interface IConfigurationProps {
  requirementId: string;
  reload?: () => void;
  reloadShortList: () => void;
  sortList: Shortlist;
  configuration: LeadRequirement;
  syncData:boolean
}
export type SearchPropertyTabRef = {
	refreshProperty: () => Promise<void>;
};

const SearchPropertyTab: React.ForwardRefRenderFunction<SearchPropertyTabRef, IConfigurationProps> = (
  props,ref
) => {
  const [cities, setCities] = useState<any>([]);
  const [microMarketsData, setMicroMarketsData] = useState<any>([]);
  const [preferredLocationsData, setPreferredLocationsData] = useState<any>([]);
  const [projectType, setProjectType] = useState<any>([]);
  const [propertyType, setPropertyType] = useState<any>([]);
  const [preferredDevelopers, setPreferredDevelopers] = useState<any>([]);
  const [floorPreferences, setFloorPreferences] = useState<any>([]);
  const [preferredProject, setPeferredProject] = useState<any>([]);
  const [amenities, setAmenities] = useState<any>([]);
  const [facilities, setFacilities] = useState<any>([]);
  const [requirementId, setRequirementId] = useState(props.requirementId);
  const [isSaveModalOpen, setSaveModalOpen] = React.useState<boolean>(false);
  const [canReset, setCanReset] = useState(false);
  const [advancedSearch, setAdvancedSearch] = React.useState<boolean>(false);
  const [formData, setFormData] = useState();
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(20);
  const [sort, setSort] = useState<ListSort>({
    sortBy: "createdAt",
    sortOrder: SortOrder.ASC,
  });

  const [cityLoaded, setCityLoaded] = useState(false);
  const [microMarketLoaded, setMicroMarketLoaded] = useState(false);
  const [preferredLocationLoaded, setPreferredLocationLoaded] = useState(false);
  const [projectTypeLoaded, setProjectTypeLoaded] = useState(false);
  const [propertyTypeLoaded, setPropertyTypeLoaded] = useState(false);

  const [onCreateRequirement, setOnCreateRequirement] = useState(false);
  const topScrollRef = React.useRef<HTMLDivElement>(null);
  const [createProposalModal, setCreateProposalModal] = useState<{
    isOpen?: boolean;
    shortListedPropertyData?: any;
  }>({ isOpen: false });
  const canSetToShortlistedPropertyTab = useSelector(
    (state: State) => state.shortListPoperty.isFromCreateProposal
  );

  const dispatch = useDispatch();

  

  const { data: requirementData, reload } = useDataLoader<LeadRequirement>(
    (reqId: string) => api.requirement.findRequirementById(reqId),
    [requirementId]
  );

  useEffect(() => {
    if (canSetToShortlistedPropertyTab) {
      handlePropertyTabChange(PropertyTabNames.ShortListedProperty);
      dispatch(setProposalTabStatus(false));
    }
  }, [canSetToShortlistedPropertyTab]);

  const {
    data: properyData,
    loading,
    loaded,
    reload: reloadPropery,
  } = useDataLoader<PaginatedApiResponse<Project> & { lastUpdatedAt: Date }>(
    (
      requId: string,
      currentPage: number,
      pageCount: number,
      searchOrder?: ListSort
    ) =>
      requId &&  api.requirement.searchProperty({
        requirementId: requId,
        page: currentPage,
        sortBy: searchOrder?.sortBy,
        sortOrder: searchOrder?.sortOrder,
        perPage: pageCount,
        isDeal: false,
      }),
    // [requirementId, page, perPage, sort]
  );

  useEffect(() => {
    if (loaded) {
      setPage(1);
      reloadPropery(requirementId, 1, perPage, sort);
    }
  }, [requirementId, sort, perPage]);

  useEffect(() => {
    if (loaded) {
      reloadPropery(requirementId, page, perPage, sort);
    }
  }, [page]);

  

  useImperativeHandle(ref, () => ({
    refreshProperty: async () => {
      if (loaded) {
        await reloadPropery(requirementId, page, perPage, sort);
      }
    },
  }));
  

  useEffect(() => {
    topScrollRef?.current?.scrollIntoView();
  }, [page, perPage]);

  const decodeHtmlEntities = (html: string) => {
    const textarea = document.createElement("textarea");
    textarea.innerHTML = html;
    return textarea.value;
  };

  React.useEffect(() => {
    if (requirementData?._id) {
      handleCityZones(requirementData.city);
      handlePreferredLocationsChange(requirementData.city);
    }
  }, [requirementData]);

  React.useEffect(() => {
    handleProjectType();
    handlePropertyType();
    fetchCityData();
  }, [requirementData]);

  const fetchCityData = async () => {
    // Load city data from API
    try {
      const cityData = await api.AddNewRequirement.loadAllCities();
      const cityOptions = cityData?.map((item: City) => ({
        value: item._id,
        name: item.cityName,
      }));
      // Set city data to the form field
      setCities(cityOptions);
      setCityLoaded(true);
    } catch (error) {
      // console.log("Failed to fetch city data:", error);
    }

    try {
      const floorData = await api.AddNewRequirement.getPreferredFloor();
      const floorDataOptions = (floorData || [])
        .map((item: any) => ({
          value: item,
          name: item,
        }))
        .sort((a: { name: number }, b: { name: number }) => a.name - b.name);
      // console.log('floorData', floorData)
      setFloorPreferences(floorDataOptions || []);
    } catch (error) {
      // console.log("Failed to fetch preferred floor:", error);
    }

    try {
      const developerData = await api.AddNewRequirement.getPreferredDevelopers({
        search: "",
      });
      const developerDataOptions = developerData?.map((item: any) => ({
        value: item._id,
        name: item.developer,
      }));

      setPreferredDevelopers(developerDataOptions);
    } catch (error) {
      // console.log("Failed to fetch preferred developers:", error);
    }

    try {
      const projectData = await api.AddNewRequirement.getPreferredProject({
        search: "",
      });
      const projectDataOptions = projectData?.map((item: any) => ({
        value: item._id,
        name: item.projectName,
      }));

      setPeferredProject(projectDataOptions);
    } catch (error) {
      // console.log("Failed to fetch preferred project:", error);
    }

    try {
      const amenityData = await api.AddNewRequirement.getAmenity({
        search: "",
      });
      const amenityDataOptions = amenityData?.map((item: any) => ({
        value: item._id,
        name: item.amenity,
      }));

      setAmenities(amenityDataOptions);
    } catch (error) {
      // console.log("Failed to fetch amenities data:", error);
    }

    try {
      const facilityData = await api.AddNewRequirement.getFacilityType();
      const facilityOptions = facilityData?.map((item: any) => ({
        value: item._id,
        name: decodeHtmlEntities(item?.facilityType),
      }));
      setFacilities(facilityOptions);
    } catch (error) {
      // console.log("Failed to fetch facilityType data:", error);
    }
  };

  // Load micro markets data from API based on selected city
  const handleCityZones = async (selectedCityId: string) => {
    handlePreferredLocationsChange(selectedCityId);
    // handlePropertyType(selectedCityId);
    // handleProjectType();
    try {
      const microMarketsListData = await api.AddNewRequirement.getCityZones(
        selectedCityId
      );
      const cityZoneOptions = microMarketsListData?.map((item: CityZone) => ({
        value: item._id,
        name: decodeHtmlEntities(item?.cityZoneName),
      }));
      setMicroMarketsData(cityZoneOptions);
      setMicroMarketLoaded(true);
      // Set micro markets data to the form field
      // setValue('microMarkets', []);
    } catch (error) {
      // console.log("Failed to fetch micro markets data:", error);
    }
  };

  // Load preferred locations data from API based on selected city
  const handlePreferredLocationsChange = async (selectedCityId: string) => {
    try {
      const preferredLocationsListData = await api.AddNewRequirement.getPreferedLocations(
        selectedCityId
      );
      const cityPreferedLocOptions = preferredLocationsListData?.map(
        (item: CityLocation) => ({
          value: item._id,
          name: decodeHtmlEntities(item?.cityLocName),
        })
      );
      setPreferredLocationsData(cityPreferedLocOptions);
      setPreferredLocationLoaded(true);
      // setValue('preferredLocations', []);
    } catch (error) {
      // console.log("Failed to fetch preferred locations data:", error);
    }
  };

  // Load property type data from API based on selected city
  const handlePropertyType = async () => {
    try {
      const propertyTypeData = await api.AddNewRequirement.getPropertyType();
      const subTypes = propertyTypeData.reduce((acc: any, item: any) => {
        acc.push(
          ...item.subTypes.map(
            (subType: { _id: any; propertyTypeName: any }) => ({
              value: subType._id,
              name: decodeHtmlEntities(subType.propertyTypeName),
            })
          )
        );
        return acc;
      }, []);

      setPropertyType(subTypes);
      setPropertyTypeLoaded(true);
    } catch (error) {
      // console.log("Failed to fetch property type data:", error);
    }
  };
  // Load project type data from API based on selected city
  const handleProjectType = async () => {
    try {
      const projectTypeData = await api.AddNewRequirement.getProjectType();
      const subTypes = projectTypeData.reduce((acc: any, item: any) => {
        if (item?.subTypes) {
          acc.push(
            ...item.subTypes.map(
              (subType: { _id: any; propertyTypeName: any }) => ({
                value: subType._id,
                name: decodeHtmlEntities(subType.propertyTypeName),
              })
            )
          );
        }
        return acc;
      }, []);
      setProjectType(subTypes);
      setProjectTypeLoaded(true);
    } catch (error) {
      // console.log("Failed to fetch project type data:", error);
    }
  };

  const { configuration } = props;

  const [propertyTabName, setTabName] = React.useState<PropertyTabNames>(
    PropertyTabNames.SearchProperty
  );

  const handlePropertyTabChange = (tabNameValue: PropertyTabNames) => {
    setTabName(tabNameValue);
  };
  const [saving, setSaving] = useState<boolean>(false);
  const onSubmit = (data: any) => {  
    const requirementV1 = {
      city: data.city,
      microMarkets: data.microMarkets,
      preferredLocations: data.preferredLocations,
      propertyType: data.propertyType,     
      projectType: data.projectType?.map((item: any) => item?.value || item),
      minPrice: advancedSearch ? data.minPrice : requirementData?.minPrice,
      maxPrice: advancedSearch ? data.maxPrice : requirementData?.maxPrice,
      // minArea: advancedSearch ? data.minArea : requirementData?.minArea,
      // maxArea: advancedSearch ? data.maxArea : requirementData?.maxArea,
      minArea: advancedSearch
        ? data.minArea || undefined
        : requirementData?.minArea || undefined,
      maxArea: advancedSearch
        ? data.maxArea || undefined
        : requirementData?.maxArea || undefined,
      earliestMoveInDate: advancedSearch
        ? data.earliestMoveInDate
        : requirementData?.earliestMoveInDate,
      lastMoveInDate: advancedSearch
        ? data.lastMoveInDate
        : requirementData?.lastMoveInDate,
      floorPreferences: advancedSearch
        ? data.floorPreferences
        : requirementData?.floorPreferences,
      preferedProjects: advancedSearch
        ? data.preferedProjects
        : requirementData?.preferedProjects,
      preferredDevelopers: advancedSearch
        ? data.preferredDevelopers
        : requirementData?.preferredDevelopers,
      constructionStatus: advancedSearch
        ? data.constructionStatus
        : requirementData?.constructionStatus,
      carParking: advancedSearch
        ? data.carParking
        : requirementData?.carParking,
      powerBackup: advancedSearch
        ? data.powerBackup
        : requirementData?.powerBackup,
      // grade: advancedSearch ? data.grade : requirementData?.grade,
      amenities: advancedSearch ? data.amenities : requirementData?.amenities,
      // proposalDueBy: advancedSearch
      //   ? data.proposalDueBy
      //   : requirementData?.proposalDueBy,
      interiorType: advancedSearch
        ? data?.interiorType
        : requirementData?.interiorType,
      facilityType: advancedSearch
        ? data?.facilityType?.value
        : requirementData?.facilityType,
      includeGradeC: advancedSearch
        ? data.includeGradeC
        : requirementData?.includeGradeC,
      includeLeaseExpiry: advancedSearch
        ? data.includeLeaseExpiry
        : requirementData?.includeLeaseExpiry,
      minSeats: advancedSearch
        ? data.minSeats || undefined
        : requirementData?.minSeats || undefined,
      maxSeats: advancedSearch
        ? data.maxSeats || undefined
        : requirementData?.maxSeats || undefined,
      constructionCompletionDate: advancedSearch
        ? data?.constructionCompletionDate
        : requirementData?.constructionCompletionDate,
    };
    const requirementV2 = {
      city: requirementData?.city,
      microMarkets: requirementData?.microMarkets,
      preferredLocations: requirementData?.preferredLocations,
      propertyType: requirementData?.propertyType,
      projectType: requirementData?.projectType,
      minPrice: requirementData?.minPrice,
      maxPrice: requirementData?.maxPrice,
      minArea: requirementData?.minArea || undefined,
      maxArea: requirementData?.maxArea || undefined,
      earliestMoveInDate: requirementData?.earliestMoveInDate,
      lastMoveInDate: requirementData?.lastMoveInDate,
      floorPreferences: requirementData?.floorPreferences,
      preferedProjects: requirementData?.preferedProjects,
      preferredDevelopers: requirementData?.preferredDevelopers,
      constructionStatus: requirementData?.constructionStatus,
      // carParking: requirementData?.carParking,
      // powerBackup: requirementData?.powerBackup,
      // grade: requirementData?.grade,
      amenities: requirementData?.amenities,
      // proposalDueBy: requirementData?.proposalDueBy,
      interiorType: requirementData?.interiorType,
      facilityType: requirementData?.facilityType,
      minSeats: requirementData?.minSeats || undefined,
      maxSeats: requirementData?.maxSeats || undefined,
      constructionCompletionDate: requirementData?.constructionCompletionDate,
      includeGradeC: requirementData?.includeGradeC,
      includeLeaseExpiry: requirementData?.includeLeaseExpiry,
    };

    const comparedDocument = diff(requirementV1, requirementV2);

    if (comparedDocument) {
      setFormData(data);
      setSaveModalOpen(true);
    } else {
      setCanReset(true);
    }
  };

  const onSave = async (data: any) => {    
    setSaving(true);
    // Call your API to save the form data
    setOnCreateRequirement(true);
    const createData = {
      city: data.city,
      microMarkets: data.microMarkets,
      preferredLocations: data.preferredLocations,
      propertyType: data.propertyType,
      projectType: data.projectType?.map((item: any) => item?.value || item),
      minPrice: data.minPrice ? Number(data.minPrice) : data.minPrice,
      maxPrice: data.maxPrice ? Number(data.maxPrice) : data.maxPrice,
      minArea: data.minArea ? Number(data.minArea) : undefined,
      maxArea: data.maxArea ? Number(data.maxArea) : undefined,
      earliestMoveInDate: data.earliestMoveInDate,
      lastMoveInDate: data.lastMoveInDate,
      floorPreferences: data.floorPreferences,
      preferedProjects: data.preferedProjects,
      preferredDevelopers: data.preferredDevelopers,
      constructionStatus: data.constructionStatus,
      carParking: data.carParking,
      powerBackup: data.powerBackup,
      includeGradeC: data.includeGradeC,
      includeLeaseExpiry: data.includeLeaseExpiry,
      // grade: data.grade,
      amenities: data.amenities,
      // proposalDueBy: data?.proposalDueBy,
      interiorType: data?.interiorType,
      facilityType: data?.facilityType?.value,
      minSeats: data?.minSeats || undefined,
      maxSeats: data?.maxSeats || undefined,
      constructionCompletionDate: data?.constructionCompletionDate,
    };

    try {
      let reponse: any;
      reponse = await api.AddNewRequirement.createRequirements({
        ...createData,
        parentId: requirementData?._id,
        leadId: requirementData?.leadId,
        customerId: requirementData?.customerId,
      });
      if (reponse) {
        // setRequirementId(reponse?._id);
        toastr.success("Success", "Requirements Edited Successfully");
        setSaveModalOpen(false);
        props.reload?.();
        topScrollRef?.current?.scrollIntoView();
      }
    } catch (error) {
      toastr.error("Error", "Failed to create requirements");
    }
    setOnCreateRequirement(false);
    setAdvancedSearch(false);
    setSaving(false);
  };

  const allLoaded =
    cityLoaded &&
    microMarketLoaded &&
    preferredLocationLoaded &&
    projectTypeLoaded &&
    propertyTypeLoaded;

    useEffect(() => {
      if(configuration?.versions?.length && !loading){

        const item = configuration.versions.reduce((prev, current) => {
          return prev.versionNumber > current.versionNumber ? prev : current;
        } , configuration.versions[0]);

        setRequirementId(item._id);
        setCityLoaded(false);
        setMicroMarketLoaded(false);
        setPreferredLocationLoaded(false);
        setProjectTypeLoaded(false);
        setPropertyTypeLoaded(false);
        reload(item._id);
        reloadPropery(item._id, page, perPage, sort);
      }
    }, [configuration?.versions])
  

  const renderSaveConfirm = () => {
    return isSaveModalOpen ? (
      <>
        <div
          className="modal fade show in"
          id="exampleModal"
          aria-labelledby="exampleModalLabel"
          aria-hidden="true"
        >
          <div className="modal-dialog modal-dialog-confirm modal-dialog-centered">
            <div className="modal-content rounded-0">
              <div className="modal-body text-center">
                <div className="container">
                  <div className="row">
                    <div className="col">
                      <p>
                        You updated the search criteria that is not permitted
                        under an approved requirement. To continue with the
                        search criteria you need to request for a requirement
                        version update.
                      </p>
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-auto mr-auto">
                      <button
                        type="button"
                        className="btn btn-outline-dark py-2 font-weight-semibold font-normal px-4 mr-1"
                        onClick={() => {
                          setSaveModalOpen(false);
                          setCanReset(true);
                        }}
                        disabled={onCreateRequirement}
                      >
                        Ignore & Reset
                      </button>
                    </div>
                    <div className="col-auto">
                      <button
                        type="button"
                        className="btn btn-primary py-2 font-weight-semibold font-normal px-4 "
                        onClick={() => onSave(formData)}
                        disabled={onCreateRequirement}
                      >
                        Yes
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="modal-backdrop fade show"></div>
      </>
    ) : (
      <></>
    );
  };
  return (
    <>
      <div className="" ref={topScrollRef}>
        <PropertyTabs
          tabName={propertyTabName}
          tabChangeHandler={handlePropertyTabChange}
          shortListedCount={props.sortList?.shortListedProperties?.length}
        />
      </div>
      {props.syncData ? (
        <ContainerLoader />
      ) : (
        <>
          {propertyTabName === PropertyTabNames.SearchProperty && (
            <>
              {!!configuration?.versions?.length && (
                <div>
                  <span className="font-xs font-weight-semibold mr-2 text-gray-600">
                    Requirement Versions
                  </span>
                  {configuration?.versions?.map((item) => {
                    return (
                      <span
                        onClick={() => {
                          if (!loading) {
                            setRequirementId(item._id);
                            setCityLoaded(false);
                            setMicroMarketLoaded(false);
                            setPreferredLocationLoaded(false);
                            setProjectTypeLoaded(false);
                            setPropertyTypeLoaded(false);
                            reload(item._id);
                          }
                        }}
                        className={classnames({
                          "border border-radius-50 px-2 py-1 font-xs font-weight-semibold rounded-pill mr-2 ": true,
                          "border-primary text-white bg-primary": requirementId === item._id,
                          "cursor-pointer": !loading,
                        })}
                      >
                        V{item.versionNumber}
                      </span>
                    );
                  })}
                </div>
              )}
              {!allLoaded && <ContainerLoader></ContainerLoader>}
              {requirementData && allLoaded && (
                <div className="col-12 col-xl-12 col-lg-12">
                  <div className="row pt-3 pb-3">
                    <SearchRequirement
                      onSubmit={onSubmit}
                      cities={cities}
                      data={requirementData}
                      microMarketsData={microMarketsData}
                      preferredLocationsData={preferredLocationsData}
                      projectType={projectType}
                      propertyType={propertyType}
                      preferredDevelopers={preferredDevelopers}
                      floorPreferences={floorPreferences}
                      preferredProject={preferredProject}
                      amenities={amenities}
                      facilities={facilities}
                      handleCityZones={handleCityZones}
                      canReset={canReset}
                      setCanReset={(re: boolean) => setCanReset(re)}
                      setAdvancedSearch={setAdvancedSearch}
                      isSaveModalOpen={isSaveModalOpen}
                      saving={saving}
                    />
                  </div>
                </div>
              )}
              {allLoaded && (
                <>
                  <SearchProperty
                    properyData={properyData!}
                    page={page}
                    perPage={perPage}
                    setPage={setPage}
                    setPerPage={setPerPage}
                    requirementVersion={requirementData?.versionNumber}
                    requirementId={requirementData?.masterRequirementId || requirementData?._id}
                    hasAnyProposalApproved={requirementData?.hasAnyProposalApproved}
                    reload={() => {
                      // handlePropertyTabChange(PropertyTabNames.ShortListedProperty);
                      props.reloadShortList();
                    }}
                    onSort={(sortOrder?: SortOrder) => {
                      setSort({ sortBy: "projectName", sortOrder });
                    }}
                    setCreateProposalModal={(slpArr) =>
                      setCreateProposalModal({
                        isOpen: true,
                        shortListedPropertyData: slpArr,
                      })
                    }
                    sortOrder={sort.sortOrder}
                    configuration={props.configuration}
                    sortList={props.sortList?.shortListedProperties}
                    allLoaded={allLoaded}
                    loading={loading!}
                  />
                </>
              )}
              {!loading && allLoaded && properyData?.data?.length === 0 && (
                <div className="py-4 d-flex flex-column align-items-center mt-5">
                  <i className="icon-box--lg font-xxl bg-orange text-white icon-bulb rounded-circle d-inline-flex justify-content-center align-items-center"></i>
                  <div
                    className="py-2 font-xs font-weight-semibold text-center my-1"
                    style={{ maxWidth: "500px", lineHeight: "21px" }}
                  >
                    We couldn't find any properties that match your criteria.
                  </div>
                </div>
              )}

              {renderSaveConfirm()}
            </>
          )}
          {propertyTabName === PropertyTabNames.ShortListedProperty && (
            <ShortListedProperty
              requirementId={requirementData?.masterRequirementId || requirementData?._id}
              sortList={props.sortList?.shortListedProperties}
              showCheckbox={true}
              reload={props.reloadShortList}
            />
          )}
          {propertyTabName === PropertyTabNames.Log && (
            <ul className="activity-timeline list-unstyled mt-5 px-4 position-relative">
              <UserLogs requirementId={requirementData?._id}></UserLogs>
            </ul>
          )}
          {createProposalModal?.isOpen &&
            (requirementData?.masterRequirementId || requirementData?._id) && (
              <CreateProposal
                requirementId={requirementData?.masterRequirementId || requirementData?._id}
                onClose={() => {
                  reloadPropery(requirementId, 1, perPage, sort);
                  setCreateProposalModal({});
                  handlePropertyTabChange(PropertyTabNames.ShortListedProperty);
                  props.reloadShortList();
                }}
                isRedirect
                selectedShortListedProperty={createProposalModal?.shortListedPropertyData || []}
              ></CreateProposal>
            )}
        </>
      )}
    </>
  );
};

export default  forwardRef<SearchPropertyTabRef,IConfigurationProps>(SearchPropertyTab);
